import Vue from 'vue';
import Component from 'vue-class-component';
import { format, parse } from 'date-fns';
import addDays from 'date-fns/addDays';
import addMonths from 'date-fns/addMonths';
import addYears from 'date-fns/addYears';
import { namespace } from 'vuex-class';

export const DEFAULT_DATE_FORMAT = 'yyyy-MM-dd';
export const DEFAULT_TIME_FORMAT = 'hh:mma';
export const DEFAULT_TIME_FORMAT_FOR_DISPLAY = 'hh:mmAM/PM';
export const DEFAULT_TIME_FORMAT_WITH_SECONDS = 'hh:mm:ssa';
export const INTERNAL_DATE_FORMAT = 'yyyy-MM-dd';
export const INTERNAL_TIME_FORMAT = 'HH:mm';
export const INTERNAL_TIME_FORMAT_WITH_SECONDS = 'HH:mm:ss';
const profile = namespace('profile');

@Component
export class FormatDateTime extends Vue {
	@profile.Getter('preferences') preferences!: any;

	get dateFormat(): string {
		return this.preferences?.dateFormat?.toString() || DEFAULT_DATE_FORMAT;
	}

	formatIsoDate(date: Date) {
		if (!date) return '';
		return date.toISOString().substring(0, 10);
	}

	static formatDateTime(
		date: Date | string,
		dateFormat?: string,
		timeFormat?: string,
	) {
		if (!date) return '';
		try {
			return format(
				new Date(date),
				dateFormat + ' ' + (timeFormat ?? DEFAULT_TIME_FORMAT),
			);
		} catch (e) {
			console.error(e);
			return '';
		}
	}

	convertDateStringToDateObject(date: string) {
		if (new Date(date).toString() !== 'Invalid Date') {
			return new Date(date);
		}

		if (date.match(/凌晨|早上|上午/)) {
			date = date.replace(/凌晨|早上|上午/, 'am');
		}
		if (date.match(/下午|晚上/)) {
			date = date.replace(/下午|晚上/i, 'pm');
		}
		const midDayIndicator = date.match(/am/i) ? 'am' : 'pm';
		const dateWithoutIndicator = date.slice(0, date.indexOf(midDayIndicator));
		const dateTimeString = `${dateWithoutIndicator} ${midDayIndicator}`;

		return parse(dateTimeString, 'yyyy-MM-dd hh:mm a', new Date());
	}

	formatDateTime(
		date: Date | string,
		dateFormat?: string,
		timeFormat?: string,
	) {
		if (!date) return '';
		return FormatDateTime.formatDateTime(
			new Date(date),
			dateFormat || this.dateFormat,
			timeFormat,
		);
	}

	static formatDate(
		date: Date | string | null | undefined,
		dateFormat: string,
	) {
		if (!date) return '';
		if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
			date = `${date}T00:00+08:00`; //TODO: assumed HKT
		}
		try {
			return format(new Date(date), dateFormat);
		} catch (e) {
			console.error(e);
			return '';
		}
	}

	formatDate(date?: Date | string | null, dateFormat?: string) {
		if (!date) return '';
		return FormatDateTime.formatDate(
			new Date(date),
			dateFormat || this.dateFormat,
		);
	}

	formatEndDate(date: Date | string) {
		if (!date) return '';
		try {
			return this.isPerpetual(date)
				? this.$t('pages.plan.options.perpetual')
				: format(new Date(date), this.dateFormat);
		} catch (e) {
			console.error(e);
			return '';
		}
	}

	get datePlaceholder(): string {
		return this.dateFormat;
	}

	get timePlaceholder(): string {
		return 'HH:MM';
	}

	static formatTime(
		time: string | Date | null | undefined,
		timeFormat: string,
	) {
		try {
			if (!time) return '';
			if (typeof time === 'string') {
				let date;
				if (time.length > 10) {
					// must be a full format
					date = new Date(time);
				} else {
					// assume AM/PM format without seconds
					date = new Date(`2000-01-01T${time}+08:00`);
				}
				return format(date, timeFormat); //TODO: assumed HKT
			} else {
				// time is Date object
				return format(time, timeFormat);
			}
		} catch (e) {
			console.error(e);
			return '';
		}
	}

	formatTime(time?: string | Date | null, timeFormat = DEFAULT_TIME_FORMAT) {
		if (!time) return '';
		return FormatDateTime.formatTime(time, timeFormat);
	}

	isPerpetual(date: Date | string): boolean {
		return Boolean(
			date && new Date(date) >= new Date(9999, 12, 31, 23, 59, 59, 999),
		);
	}

	isDateAfter(
		before: string | Date,
		after: string | Date,
		years: number,
		months: number,
		days: number,
	) {
		const beforeDate = new Date(before);
		const afterDate = new Date(after);
		const compare = addYears(
			addMonths(addDays(beforeDate, days), months),
			years,
		);
		return afterDate.getTime() - compare.getTime() >= 0;
	}

	findLatestTime(dates: Date[]): Date | undefined {
		return dates.sort(
			(a, b) => new Date(b).getTime() - new Date(a).getTime(),
		)[0];
	}

	formatSpecialRelativeTime(
		date: string | Date,
		timeFormat: string = DEFAULT_TIME_FORMAT,
		isShortForm = false,
	) {
		if (!date) {
			return '';
		}

		let i18nSuffix = '';

		if (isShortForm) {
			i18nSuffix = '.short';
		}

		try {
			// Convert to Date object if string
			const targetDate = typeof date === 'string' ? new Date(date) : date;
			const now = new Date();

			// Calculate difference in milliseconds
			const diffMs = now.getTime() - targetDate.getTime();

			// Case 1: within 1 minute (0 to 59,999 ms)
			if (diffMs >= 0 && diffMs < 60000) {
				return this.$t(`date.special.relative${i18nSuffix}.justNow`);
			}

			// Calculate minutes
			const diffMinutes = Math.floor(diffMs / 60000);

			// Case 2: 1 minute to 59 minutes ago
			if (diffMinutes >= 1 && diffMinutes < 60) {
				return this.$t(`date.special.relative${i18nSuffix}.minutesAgo`, {
					n: diffMinutes,
				});
			}

			// Calculate hours
			const diffHours = Math.floor(diffMs / 3600000);

			// Case 3: 1 hour to 23:59 hours ago (up to 23 hours and 59 minutes)
			if (diffHours >= 1 && diffHours < 24) {
				return this.$t(`date.special.relative${i18nSuffix}.hoursAgo`, {
					n: diffHours,
				});
			}

			// Calculate days
			const diffDays = Math.floor(diffMs / 86400000);

			// Format time from the target date
			const timeStr = format(targetDate, timeFormat);

			// Case 4: 24-47:59:59 hours ago (1 day to 1 day 23:59:59)
			if (diffDays === 1) {
				return this.$t(`date.special.relative${i18nSuffix}.yesterday`, {
					time: timeStr,
				});
			}

			// Case 5: 48-71:59:59 hours ago (2 days to 2 days 23:59:59)
			if (diffDays === 2 || isShortForm) {
				return this.$t(`date.special.relative${i18nSuffix}.twoDaysAgo`, {
					n: diffDays,
					time: timeStr,
				});
			}

			// Case 6: 72-95:59:59 hours ago (3 days to 3 days 23:59:59)
			if (diffDays === 3) {
				return this.$t(`date.special.relative.threeDaysAgo`, {
					time: timeStr,
				});
			}

			// Case 7: more than 96 hours ago
			return this.formatDateTime(targetDate, undefined, timeFormat);
		} catch (e) {
			console.error('formatSpecialRelativeTime:', date, e);
			return '';
		}
	}

	formatSpecialRelativeTimeShort(
		date: string | Date,
		timeFormat: string = DEFAULT_TIME_FORMAT,
	) {
		return this.formatSpecialRelativeTime(date, timeFormat, true);
	}

	// testFormatDateTime() {
	// 	console.info('Test FormatDateTime');
	// 	console.assert(
	// 		!this.isDateAfter('2022-01-01', '2022-02-01', 0, 1, 0),
	// 		'case 1',
	// 	);
	// 	console.assert(
	// 		!this.isDateAfter('2022-01-31', '2022-02-01', 0, 1, 0),
	// 		'case 2',
	// 	);
	// 	console.assert(
	// 		!this.isDateAfter('2022-01-01', '2027-04-01', 5, 3, 0) &&
	// 			this.isDateAfter('2022-01-01', '2027-04-02', 5, 3, 0),
	// 		'case 3',
	// 	);
	// 	console.assert(
	// 		this.isDateAfter(
	// 			'2016-01-01',
	// 			this.formatDate(new Date(), 'yyyy-MM-dd'),
	// 			5,
	// 			3,
	// 			0,
	// 		),
	// 		'case 4',
	// 	);
	// 	console.assert(
	// 		!this.isDateAfter(new Date('2022-01-01'), '2027-04-01', 5, 3, 0) &&
	// 			this.isDateAfter(new Date('2022-01-01'), '2027-04-02', 5, 3, 0),
	// 		'case 5',
	// 	);
	// 	console.info('Done');
	// }
}
